import { isEqual } from "lodash";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { useUpdateAgentVersion } from "src/agents/queries/management";
import { AgentVersionConfigurationInput } from "src/agents/types";
import { toastActions } from "src/design-system/Toast/utils";
import { debounceAndSaveArgs } from "src/utils/debounceAndSaveArgs";

/**
 * These helpers filter out incomplete/invalid tools before saving to prevent
 * server-side validation errors when the user is in the middle of editing
 * tool JSON configurations.
 *
 * TEMPORARY BANDAID: remove once we have a proper UI for tool configs/setup
 * that validates tools before allowing them to be added to the form state.
 */
const isValidTool = (tool: unknown): boolean => {
  if (!tool || typeof tool !== "object") return false;
  const t = tool as Record<string, unknown>;
  // At minimum, a tool needs a name to be considered valid for saving
  return typeof t.name === "string" && t.name.trim() !== "";
};

const filterValidTools = (
  config: AgentVersionConfigurationInput,
): AgentVersionConfigurationInput => {
  if (!config.tools) return config;
  return {
    ...config,
    tools: config.tools.filter(isValidTool),
  };
};

/**
 * Wraps useUpdateAgentVersion with debounce + sequential queue logic.
 * - Uses the existing mutation
 * - Adds debounce to prevent excessive API calls
 * - Adds queue behavior via pendingConfigRef
 * - Tracks etag in ref to avoid stale closures
 */
type UseSequentialAutoSaveOptions = {
  agentId: string;
  versionId: string;
  name: string;
  description: string;
  initialEtag: string;
  disabled?: boolean;
  onSave?: () => void;
};

export const useSequentialAutoSave = ({
  agentId,
  versionId,
  name,
  description,
  initialEtag,
  disabled,
  onSave,
}: UseSequentialAutoSaveOptions) => {
  // Use the existing mutation - it already handles cache updates, optimistic updates, rollback
  const updateMutation = useUpdateAgentVersion();

  // Stores latest data waiting to be sent if there's already an ongoing save call
  const pendingConfigRef = useRef<AgentVersionConfigurationInput | null>(null);

  // Track the last successfully saved configuration to avoid redundant saves
  const lastSavedConfigRef = useRef<AgentVersionConfigurationInput | null>(
    null,
  );

  // Track latest etag - updated after each successful save
  const etagRef = useRef(initialEtag);

  // Keep etag in sync when prop changes (from query cache updates)
  useEffect(() => {
    etagRef.current = initialEtag;
  }, [initialEtag]);

  const saveWithQueue = useCallback(
    (configuration: AgentVersionConfigurationInput) => {
      updateMutation.mutate(
        {
          agentId,
          versionId,
          name,
          description,
          configuration,
          etag: etagRef.current,
        },
        {
          onSuccess: (result) => {
            // Update etag ref with new value from server
            etagRef.current = result.etag;

            // Track what we saved to avoid redundant saves
            // Must deep clone because react-hook-form mutates the same object reference
            lastSavedConfigRef.current = structuredClone(configuration);

            // Notify caller of successful save (e.g., for pre-warming playground)
            onSave?.();
          },
          onSettled: (_data, error) => {
            // THE QUEUE LOGIC: When request finishes, check if there was any pending save call
            if (pendingConfigRef.current !== null && !error) {
              const nextConfig = pendingConfigRef.current;
              pendingConfigRef.current = null;
              // Fire next save with fresh etag
              saveWithQueue(nextConfig);
            } else if (error) {
              // Clear pending on error to avoid stale/erroneous data being saved
              pendingConfigRef.current = null;
            }
          },
          onError: (error) => {
            toastActions.failure({
              title: "Failed to save changes",
              description:
                error instanceof Error
                  ? error.message
                  : "An unknown error occurred",
            });
          },
        },
      );
    },

    // This dependency array is intentionally not exhaustive to avoid unnecessary re-renders
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [updateMutation.mutate, onSave, agentId, versionId, name, description],
  );

  // The trigger function - checks if busy before firing
  const isPendingRef = useRef(false); // To avoid save being new every render
  isPendingRef.current = updateMutation.isPending;
  const save = useCallback(
    (configuration: AgentVersionConfigurationInput) => {
      if (disabled) return;

      // TEMPORARY BANDAID: Filter out incomplete tools to prevent server
      // validation errors while user is editing JSON.
      const validConfig = filterValidTools(configuration);

      // Skip save if configuration hasn't actually changed
      // This prevents redundant API calls when form subscription fires
      // multiple times with the same effective value
      if (isEqual(validConfig, lastSavedConfigRef.current)) {
        return;
      }

      if (isPendingRef.current) {
        // BUSY: Queue it (overwrites previous - latest wins)
        pendingConfigRef.current = validConfig;
      } else {
        // IDLE: Fire immediately
        saveWithQueue(validConfig);
      }
    },
    [saveWithQueue, disabled],
  );

  // Debounced save
  const debouncedSave = useMemo(() => debounceAndSaveArgs(save, 500), [save]);

  // Pending state: debounce waiting OR mutation in-flight OR waiting room has data
  const hasPendingChanges =
    debouncedSave.pending() ||
    updateMutation.isPending ||
    pendingConfigRef.current !== null;

  return {
    save: debouncedSave,
    hasPendingChanges,
    isSaving: updateMutation.isPending,
  };
};
